import React, { useEffect, useState, useCallback, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import PidPLUModal from '../components/PidPLUModal';
import PidTableShell from '../components/PidTableShell';
import {
  PageWrapper,
  ContentWrapper,
  SizeTagWrapper,
  SizeTag,
} from '../styles/PackageIndexStyles';
import StatusPicker from '../components/StatusPicker';
import { PID_STATUSES, pidStatusColor, normalizePidStatus, pidStatusRank } from '../utils/pidStatus';
import { changePidStatus } from '../utils/pidStatusApi';

const PidPLUPage = () => {
  const [loading, setLoading] = useState(true);
  const [groupedData, setGroupedData] = useState([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [sizeMap, setSizeMap] = useState({});

  const fetchPLUs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/api/pidplu');
      const rows = res.data || [];

      const map = {};
      rows.forEach(r => {
        if (!map[r.LabelID]) {
          map[r.LabelID] = {
            LabelID: r.LabelID,
            Description: r.Description,
            GS1DataBar: r.GS1DataBar,
            Status: r.Status,
            Items: [],
          };
        }
        map[r.LabelID].Items.push(r);
      });

      setGroupedData(Object.values(map));
    } catch (err) {
      console.error('[GET /api/pidplu]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSizes = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/sizeoptions/dropdown');
      const map = {};
      res.data.forEach(opt => {
        map[opt.UniqueID] = opt.SizeOption;
      });
      setSizeMap(map);
    } catch (err) {
      console.error('[GET /sizeoptions/dropdown]', err);
    }
  }, []);

  useEffect(() => {
    fetchPLUs();
    fetchSizes();
  }, [fetchPLUs, fetchSizes]);

  const handleSave = () => {
    setShowAddPanel(false);
    fetchPLUs();
  };

  const handleEditSave = () => {
    setEditGroup(null);
    fetchPLUs();
  };

  const handleStatus = useCallback(async (group, next) => {
    const key = group.LabelID;
    const prev = group.Status;
    setGroupedData(gs => gs.map(g => (g.LabelID === key
      ? { ...g, Status: next, Items: (g.Items || []).map(it => ({ ...it, Status: next })) }
      : g)));
    try {
      await changePidStatus('plu', key, next); // PLU never blocks
    } catch (e) {
      console.error('[PidPLU] status change failed', e);
      setGroupedData(gs => gs.map(g => (g.LabelID === key
        ? { ...g, Status: prev, Items: (g.Items || []).map(it => ({ ...it, Status: prev })) }
        : g)));
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  }, []);

  const columns = useMemo(() => [
    {
      key: 'Status', label: 'Status', interactive: true, cardRole: 'status',
      sortValue: (g) => pidStatusRank(g.Status),
      render: (g) => (
        <StatusPicker
          status={g.Status}
          confirm
          size="sm"
          statuses={PID_STATUSES}
          colorFor={pidStatusColor}
          normalize={normalizePidStatus}
          ariaLabel={`Status for label ${g.LabelID}`}
          onChange={(next) => handleStatus(g, next)}
        />
      ),
    },
    { key: 'GS1DataBar', label: 'GS1 DataBar', sortValue: (g) => g.GS1DataBar || '' },
    { key: 'LabelID', label: 'Label ID', emphasis: true, cardRole: 'title', sortValue: (g) => g.LabelID || '' },
    { key: 'Description', label: 'Description', sortValue: (g) => g.Description || '' },
    {
      key: 'CommoditySizes', label: 'Commodity / Sizes',
      sortValue: (g) => g.Items?.[0]?.Commodity || '',
      render: (g) => (
        <div>
          {g.Items.map((item, idx) => (
            <div
              key={`${g.LabelID}-${item.Commodity}-${idx}`}
              style={{
                borderTop: idx === 0 ? 'none' : '1px solid var(--shadow-a05)',
                padding: idx === 0 ? '0 0 0.5rem' : '0.5rem 0'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                {item.Commodity}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <SizeTagWrapper style={{ justifyContent: 'flex-start' }}>
                  {item.SizeOptionIDs?.split(',')
                    .filter(Boolean)
                    .map((id, i) => (
                      <SizeTag
                        key={`size-${i}`}
                        style={{ background: 'var(--green-600)', color: 'var(--white)' }}
                      >
                        {sizeMap[id] || id}
                      </SizeTag>
                    ))}
                </SizeTagWrapper>

                <SizeTagWrapper style={{ justifyContent: 'flex-start' }}>
                  {item.ImportSizeIDs?.split(',')
                    .filter(Boolean)
                    .map((id, i) => (
                      <SizeTag
                        key={`import-${i}`}
                        style={{ background: 'var(--grey-700)', color: 'var(--white)' }}
                      >
                        {sizeMap[id] || id}
                      </SizeTag>
                    ))}
                </SizeTagWrapper>
              </div>
            </div>
          ))}
        </div>
      ),
    },
  ], [handleStatus, sizeMap]);

  const searchText = useCallback((g) => {
    const parts = [g.Status, g.GS1DataBar, g.LabelID, g.Description];
    (g.Items || []).forEach(item => {
      parts.push(item.Commodity);
      String(item.SizeOptionIDs || '').split(',').filter(Boolean).forEach(id => parts.push(sizeMap[id] || id));
      String(item.ImportSizeIDs || '').split(',').filter(Boolean).forEach(id => parts.push(sizeMap[id] || id));
    });
    return parts.filter(Boolean).join(' ');
  }, [sizeMap]);

  return (
    <PageWrapper>
      <ContentWrapper>
        <PidTableShell
          title="PID PLU"
          columns={columns}
          rows={groupedData}
          rowKey={(g) => g.LabelID}
          searchText={searchText}
          defaultSort={{ key: 'GS1DataBar', dir: 'asc' }}
          onAdd={() => setShowAddPanel(true)}
          onRowClick={(g) => setEditGroup(g)}
          rowAriaLabel={(g) => `Edit PLU label ${g.LabelID || ''}`}
          loading={loading}
          loadingText="Loading PID PLUs..."
          emptyText="No PID PLU records found."
        />

        {showAddPanel && (
          <PidPLUModal mode="create" onClose={() => setShowAddPanel(false)} onSave={handleSave} />
        )}

        {editGroup && (
          <PidPLUModal
            mode="edit"
            item={editGroup.Items[0]}
            onClose={() => setEditGroup(null)}
            onSave={handleEditSave}
          />
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export default PidPLUPage;
